/**
 * Super Admin - Property Owner Profile Page
 * Professional profile view with documents and properties
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { superAdminAPI } from "utils/api";

// Context
import { useAuth } from "context/authContext";

function PropertyOwnerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [owner, setOwner] = useState(null);
  const [properties, setProperties] = useState([]);
  const [editNotes, setEditNotes] = useState("");
  const [editType, setEditType] = useState("Standard");
  const [activeTab, setActiveTab] = useState(0);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    fetchOwnerProfile();
  }, [id]);

  const fetchOwnerProfile = async () => {
    try {
      setLoading(true);
      const response = await superAdminAPI.getPropertyOwnerById(id);
      const ownerData = response.owner || response.data;
      if (ownerData) {
        setOwner(ownerData);
        setProperties(ownerData.properties || []);
        setEditNotes(ownerData.notes || "");
        setEditType(ownerData.type || "Standard");
      }
    } catch (error) {
      console.error("Error fetching property owner:", error);
      setMessage({
        type: "error",
        text: "Failed to load property owner profile. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      // Validate type before sending
      if (editType && !["Standard", "VIP"].includes(editType)) {
        setMessage({
          type: "error",
          text: "Invalid owner type. Must be Standard or VIP.",
        });
        setSubmitting(false);
        return;
      }

      const response = await superAdminAPI.updatePropertyOwner(id, {
        type: editType || "Standard",
        notes: editNotes || "",
      });

      if (response.message) {
        setMessage({
          type: "success",
          text: response.message || "Property owner updated successfully!",
        });
        await fetchOwnerProfile();
      } else if (response.error) {
        setMessage({
          type: "error",
          text: response.error || "Failed to update property owner.",
        });
      }
    } catch (error) {
      console.error("Error updating property owner:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update property owner. Please try again.";
      setMessage({
        type: "error",
        text: errorMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "success";
      case "Pending":
        return "warning";
      case "Rejected":
        return "error";
      case "Suspended":
        return "dark";
      default:
        return "secondary";
    }
  };

  const getPropertyStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "success";
      case "Pending":
        return "warning";
      case "Inspection":
        return "info";
      case "Inspection_Completed":
        return "primary";
      case "Rejected":
        return "error";
      case "Disabled":
        return "dark";
      default:
        return "secondary";
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Helper function to extract multilingual text
  const getMultilingualText = (field, defaultText = "N/A") => {
    if (!field) return defaultText;
    if (typeof field === "string") return field;
    if (typeof field === "object") {
      // Handle multilingual object {en: "...", ar: "..."}
      return field.en || field.ar || defaultText;
    }
    return defaultText;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!owner) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Alert severity="error">Property owner not found</Alert>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => navigate("/super-admin/property-owners")}
            sx={{ mt: 2 }}
          >
            Back to Property Owners
          </MDButton>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header */}
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox display="flex" alignItems="center" gap={2}>
            <MDButton
              variant="outlined"
              color="info"
              onClick={() => {
                // Navigate back based on user role
                if (userRole === "Admin") {
                  navigate("/admin/property-owners");
                } else {
                  navigate("/super-admin/property-owners");
                }
              }}
              startIcon={<Icon>arrow_back</Icon>}
            >
              Back
            </MDButton>
            <MDTypography variant="h4" fontWeight="bold">
              Property Owner Profile
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Alert Messages */}
        {message.text && (
          <MDBox mb={3}>
            <Alert severity={message.type} onClose={() => setMessage({ type: "", text: "" })}>
              {message.text}
            </Alert>
          </MDBox>
        )}

        {/* Profile Header Card */}
        <Card sx={{ mb: 3, overflow: "hidden" }}>
          <MDBox
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              p: 4,
              position: "relative",
            }}
          >
            <MDBox
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                width: "300px",
                height: "300px",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "50%",
                transform: "translate(30%, -30%)",
              }}
            />
            <Grid container spacing={3} position="relative">
              <Grid item xs={12} md={3}>
                <MDBox display="flex" justifyContent="center">
                  <Avatar
                    src={owner.personalPhoto?.url}
                    sx={{
                      width: 120,
                      height: 120,
                      bgcolor: owner.personalPhoto?.url
                        ? "transparent"
                        : "rgba(255, 255, 255, 0.2)",
                      fontSize: "3rem",
                      fontWeight: "bold",
                      border: "4px solid white",
                    }}
                  >
                    {!owner.personalPhoto?.url && getInitials(owner.fullName)}
                  </Avatar>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={9}>
                <MDBox>
                  <MDBox display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
                    <MDTypography variant="h3" fontWeight="bold" color="white">
                      {owner.fullName}
                    </MDTypography>
                    <Chip
                      label={owner.type || "Standard"}
                      color={owner.type === "VIP" ? "warning" : "default"}
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.2)",
                        color: "white",
                        fontWeight: "bold",
                        border: "1px solid white",
                      }}
                    />
                    <Chip
                      label={owner.status || "Pending"}
                      color={getStatusColor(owner.status)}
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.2)",
                        color: "white",
                        fontWeight: "bold",
                        border: "1px solid white",
                      }}
                    />
                  </MDBox>
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ color: "white" }}>email</Icon>
                      <MDTypography variant="body1" color="white">
                        {owner.email}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ color: "white" }}>phone</Icon>
                      <MDTypography variant="body1" color="white">
                        {owner.phone || "N/A"}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ color: "white" }}>verified</Icon>
                      <MDTypography variant="body1" color="white">
                        Email: {owner.emailVerified ? "Verified" : "Not Verified"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </Card>

        {/* Tabs */}
        <Card sx={{ mb: 3, overflow: "hidden" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: { xs: 48, sm: 64 },
                "& .MuiTab-root": {
                  minHeight: { xs: 48, sm: 64 },
                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                },
              }}
            >
              <Tab label="Overview" />
              <Tab label={`Properties (${properties.length})`} />
              <Tab label="Documents" />
              <Tab label="Edit Information" />
            </Tabs>
          </Box>

          <MDBox p={{ xs: 2, sm: 3 }}>
            {/* Overview Tab */}
            {activeTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <MDBox mb={3}>
                    <MDTypography variant="h6" fontWeight="bold" mb={2}>
                      Account Information
                    </MDTypography>
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <MDBox mb={2}>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Registration Date
                        </MDTypography>
                        <MDTypography variant="body1" fontWeight="medium">
                          {owner.createdAt
                            ? new Date(owner.createdAt).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : "N/A"}
                        </MDTypography>
                      </MDBox>
                      <MDBox mb={2}>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Status
                        </MDTypography>
                        <Chip
                          label={owner.status || "Pending"}
                          color={getStatusColor(owner.status)}
                          size="small"
                        />
                      </MDBox>
                      <MDBox mb={2}>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Owner Type
                        </MDTypography>
                        <Chip
                          label={owner.type || "Standard"}
                          color={owner.type === "VIP" ? "warning" : "default"}
                          size="small"
                        />
                      </MDBox>
                      <MDBox>
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.5}
                        >
                          Email Verification
                        </MDTypography>
                        <Chip
                          label={owner.emailVerified ? "Verified" : "Not Verified"}
                          color={owner.emailVerified ? "success" : "warning"}
                          size="small"
                        />
                      </MDBox>
                    </Paper>
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={6}>
                  <MDBox mb={3}>
                    <MDTypography variant="h6" fontWeight="bold" mb={2}>
                      Personal Information
                    </MDTypography>
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      {owner.nationality && (
                        <MDBox mb={2}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Nationality
                          </MDTypography>
                          <MDTypography variant="body1" fontWeight="medium">
                            {owner.nationality}
                          </MDTypography>
                        </MDBox>
                      )}
                      {owner.jobType && (
                        <MDBox mb={2}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Job Type
                          </MDTypography>
                          <MDTypography variant="body1" fontWeight="medium">
                            {owner.jobType}
                          </MDTypography>
                        </MDBox>
                      )}
                      {owner.address && (
                        <MDBox mb={2}>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Address
                          </MDTypography>
                          <MDTypography variant="body1" fontWeight="medium">
                            {owner.address}
                          </MDTypography>
                        </MDBox>
                      )}
                      {owner.gender && (
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Gender
                          </MDTypography>
                          <MDTypography variant="body1" fontWeight="medium">
                            {owner.gender}
                          </MDTypography>
                        </MDBox>
                      )}
                    </Paper>
                  </MDBox>
                </Grid>

                {/* Bank Details Section */}
                {owner.bankDetails &&
                  (owner.bankDetails.bankName ||
                    owner.bankDetails.accountHolderName ||
                    owner.bankDetails.accountNumber ||
                    owner.bankDetails.iban ||
                    owner.bankDetails.swiftCode ||
                    owner.bankDetails.branchName) && (
                    <Grid item xs={12}>
                      <MDBox mb={3}>
                        <MDTypography variant="h6" fontWeight="bold" mb={2}>
                          Bank Details
                        </MDTypography>
                        <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                          <Grid container spacing={2}>
                            {owner.bankDetails.bankName && (
                              <Grid item xs={12} md={6}>
                                <MDBox mb={2}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mb={0.5}
                                  >
                                    Bank Name
                                  </MDTypography>
                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    <Icon sx={{ fontSize: "1.2rem", color: "text.secondary" }}>
                                      account_balance
                                    </Icon>
                                    <MDTypography variant="body1" fontWeight="medium">
                                      {owner.bankDetails.bankName}
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>
                              </Grid>
                            )}
                            {owner.bankDetails.accountHolderName && (
                              <Grid item xs={12} md={6}>
                                <MDBox mb={2}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mb={0.5}
                                  >
                                    Account Holder Name
                                  </MDTypography>
                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    <Icon sx={{ fontSize: "1.2rem", color: "text.secondary" }}>
                                      person
                                    </Icon>
                                    <MDTypography variant="body1" fontWeight="medium">
                                      {owner.bankDetails.accountHolderName}
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>
                              </Grid>
                            )}
                            {owner.bankDetails.accountNumber && (
                              <Grid item xs={12} md={6}>
                                <MDBox mb={2}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mb={0.5}
                                  >
                                    Account Number
                                  </MDTypography>
                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    <Icon sx={{ fontSize: "1.2rem", color: "text.secondary" }}>
                                      credit_card
                                    </Icon>
                                    <MDTypography
                                      variant="body1"
                                      fontWeight="medium"
                                      sx={{ fontFamily: "monospace" }}
                                    >
                                      {owner.bankDetails.accountNumber}
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>
                              </Grid>
                            )}
                            {owner.bankDetails.iban && (
                              <Grid item xs={12} md={6}>
                                <MDBox mb={2}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mb={0.5}
                                  >
                                    IBAN
                                  </MDTypography>
                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    <Icon sx={{ fontSize: "1.2rem", color: "text.secondary" }}>
                                      account_balance_wallet
                                    </Icon>
                                    <MDTypography
                                      variant="body1"
                                      fontWeight="medium"
                                      sx={{ fontFamily: "monospace" }}
                                    >
                                      {owner.bankDetails.iban}
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>
                              </Grid>
                            )}
                            {owner.bankDetails.swiftCode && (
                              <Grid item xs={12} md={6}>
                                <MDBox mb={2}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mb={0.5}
                                  >
                                    SWIFT Code
                                  </MDTypography>
                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    <Icon sx={{ fontSize: "1.2rem", color: "text.secondary" }}>
                                      code
                                    </Icon>
                                    <MDTypography
                                      variant="body1"
                                      fontWeight="medium"
                                      sx={{ fontFamily: "monospace" }}
                                    >
                                      {owner.bankDetails.swiftCode}
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>
                              </Grid>
                            )}
                            {owner.bankDetails.branchName && (
                              <Grid item xs={12} md={6}>
                                <MDBox mb={2}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mb={0.5}
                                  >
                                    Branch Name
                                  </MDTypography>
                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    <Icon sx={{ fontSize: "1.2rem", color: "text.secondary" }}>
                                      location_on
                                    </Icon>
                                    <MDTypography variant="body1" fontWeight="medium">
                                      {owner.bankDetails.branchName}
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>
                              </Grid>
                            )}
                          </Grid>
                        </Paper>
                      </MDBox>
                    </Grid>
                  )}

                {owner.notes && (
                  <Grid item xs={12}>
                    <MDBox>
                      <MDTypography variant="h6" fontWeight="bold" mb={2}>
                        Notes
                      </MDTypography>
                      <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                        <MDTypography variant="body1">{owner.notes}</MDTypography>
                      </Paper>
                    </MDBox>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Properties Tab */}
            {activeTab === 1 && (
              <MDBox>
                {properties.length === 0 ? (
                  <MDBox textAlign="center" py={6}>
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>home</Icon>
                    <MDTypography variant="h6" color="text.secondary" mb={1}>
                      No Properties Found
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      This owner hasn't added any properties yet.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <Grid container spacing={3}>
                    {properties.map((property) => (
                      <Grid item xs={12} md={6} lg={4} key={property._id || property.id}>
                        <Card
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            transition: "transform 0.2s, box-shadow 0.2s",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: 4,
                            },
                            borderLeft: "4px solid",
                            borderColor: `${getPropertyStatusColor(property.status)}.main`,
                          }}
                        >
                          <MDBox p={2.5} sx={{ flexGrow: 1 }}>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="start"
                              mb={1.5}
                            >
                              <MDTypography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
                                {getMultilingualText(property.title, "Untitled Property")}
                              </MDTypography>
                              <Chip
                                label={property.status || "Unknown"}
                                color={getPropertyStatusColor(property.status)}
                                size="small"
                              />
                            </MDBox>
                            <MDTypography variant="body2" color="text.secondary" mb={1.5}>
                              {property.propertyType
                                ? property.propertyType.charAt(0).toUpperCase() +
                                  property.propertyType.slice(1)
                                : "N/A"}
                            </MDTypography>
                            <MDBox display="flex" alignItems="center" mb={1.5}>
                              <Icon sx={{ fontSize: "1rem", color: "text.secondary", mr: 0.5 }}>
                                location_on
                              </Icon>
                              <MDTypography variant="body2" color="text.secondary">
                                {getMultilingualText(property.address, "N/A")}
                                {getMultilingualText(property.city) !== "N/A"
                                  ? `, ${getMultilingualText(property.city)}`
                                  : ""}
                              </MDTypography>
                            </MDBox>
                            <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
                              {property.rooms && (
                                <Chip
                                  icon={<Icon sx={{ fontSize: "1rem !important" }}>bed</Icon>}
                                  label={`${property.rooms} Rooms`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {property.bathrooms && (
                                <Chip
                                  icon={<Icon sx={{ fontSize: "1rem !important" }}>bathroom</Icon>}
                                  label={`${property.bathrooms} Bath`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {property.price && (
                                <Chip
                                  icon={
                                    <Icon sx={{ fontSize: "1rem !important" }}>attach_money</Icon>
                                  }
                                  label={`$${property.price.toLocaleString()}`}
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                />
                              )}
                            </MDBox>
                            <MDButton
                              variant="outlined"
                              color="info"
                              size="small"
                              fullWidth
                              onClick={() =>
                                navigate(`/admin/properties/${property._id || property.id}`)
                              }
                            >
                              View Details
                            </MDButton>
                          </MDBox>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </MDBox>
            )}

            {/* Documents Tab */}
            {activeTab === 2 && (
              <MDBox>
                <Grid container spacing={3}>
                  {/* Official Documents */}
                  {owner.officialDocuments && owner.officialDocuments.length > 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="h6" fontWeight="bold" mb={2}>
                        Official Documents
                      </MDTypography>
                      <Grid container spacing={2}>
                        {owner.officialDocuments.map((doc, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card sx={{ p: 2 }}>
                              <MDBox display="flex" alignItems="center" gap={2}>
                                <Icon sx={{ fontSize: "2.5rem", color: "error.main" }}>
                                  description
                                </Icon>
                                <MDBox flex={1}>
                                  <MDTypography variant="body2" fontWeight="medium">
                                    {doc.name || `Document ${index + 1}`}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text.secondary">
                                    {doc.uploadedAt
                                      ? new Date(doc.uploadedAt).toLocaleDateString()
                                      : "N/A"}
                                  </MDTypography>
                                </MDBox>
                                <Link href={doc.url} target="_blank" rel="noopener noreferrer">
                                  <IconButton size="small" color="info">
                                    <Icon>open_in_new</Icon>
                                  </IconButton>
                                </Link>
                              </MDBox>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}

                  {/* Document Bucket */}
                  {owner.documentBucket && owner.documentBucket.length > 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="h6" fontWeight="bold" mb={2}>
                        Additional Documents
                      </MDTypography>
                      <Grid container spacing={2}>
                        {owner.documentBucket.map((doc, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card sx={{ p: 2 }}>
                              <MDBox display="flex" alignItems="center" gap={2}>
                                <Icon sx={{ fontSize: "2.5rem", color: "info.main" }}>folder</Icon>
                                <MDBox flex={1}>
                                  <MDTypography variant="body2" fontWeight="medium">
                                    {doc.name || `Document ${index + 1}`}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text.secondary">
                                    Uploaded by: {doc.uploadedBy || "N/A"}
                                  </MDTypography>
                                  {doc.uploadedAt && (
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      {new Date(doc.uploadedAt).toLocaleDateString()}
                                    </MDTypography>
                                  )}
                                </MDBox>
                                <Link href={doc.url} target="_blank" rel="noopener noreferrer">
                                  <IconButton size="small" color="info">
                                    <Icon>open_in_new</Icon>
                                  </IconButton>
                                </Link>
                              </MDBox>
                              {doc.notes && (
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                  mt={1}
                                  display="block"
                                >
                                  {doc.notes}
                                </MDTypography>
                              )}
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}

                  {/* ID Card */}
                  {owner.idCard && owner.idCard.url && (
                    <Grid item xs={12} sx={{ minWidth: 0 }}>
                      <MDTypography variant="h6" fontWeight="bold" mb={2}>
                        ID Card
                      </MDTypography>
                      <Card sx={{ p: { xs: 2, sm: 2 }, overflow: "hidden" }}>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          flexWrap="wrap"
                          gap={2}
                          sx={{ "& > *": { minWidth: 0 } }}
                        >
                          <Icon sx={{ fontSize: "2.5rem", color: "primary.main", flexShrink: 0 }}>
                            badge
                          </Icon>
                          <MDBox flex="1 1 120px" minWidth={0}>
                            <MDTypography variant="body2" fontWeight="medium">
                              ID Card (PDF)
                            </MDTypography>
                          </MDBox>
                          <MDBox
                            display="flex"
                            gap={1}
                            flexWrap="wrap"
                            sx={{
                              flex: { xs: "1 1 100%", sm: "0 0 auto" },
                              flexDirection: { xs: "column", sm: "row" },
                              "& .MuiButton-root": { minWidth: { xs: "100%", sm: "auto" } },
                            }}
                          >
                            <MDButton
                              variant="outlined"
                              color="info"
                              size="small"
                              fullWidth
                              sx={{ width: { xs: "100%", sm: "auto" } }}
                              startIcon={<Icon>open_in_new</Icon>}
                              onClick={() => {
                                if (owner.idCard?.url) {
                                  const url = owner.idCard.url;
                                  // Check if URL is valid
                                  if (
                                    url &&
                                    (url.startsWith("http://") || url.startsWith("https://"))
                                  ) {
                                    // Open PDF in new window with PDF viewer
                                    const newWindow = window.open("", "_blank");
                                    if (newWindow) {
                                      newWindow.location.href = url;
                                    } else {
                                      // If popup blocked, try direct navigation
                                      window.open(url, "_blank", "noopener,noreferrer");
                                    }
                                  } else {
                                    setMessage({
                                      type: "error",
                                      text: "Invalid ID card URL. Please contact support.",
                                    });
                                  }
                                }
                              }}
                            >
                              View ID Card
                            </MDButton>
                            <MDButton
                              variant="outlined"
                              color="secondary"
                              size="small"
                              fullWidth
                              sx={{ width: { xs: "100%", sm: "auto" } }}
                              startIcon={<Icon>download</Icon>}
                              onClick={() => {
                                if (owner.idCard?.url) {
                                  const url = owner.idCard.url;
                                  if (
                                    url &&
                                    (url.startsWith("http://") || url.startsWith("https://"))
                                  ) {
                                    // Create a temporary anchor element to trigger download
                                    const link = document.createElement("a");
                                    link.href = url;
                                    link.download = `ID_Card_${
                                      owner.fullName?.replace(/\s+/g, "_") || "document"
                                    }.pdf`;
                                    link.target = "_blank";
                                    link.rel = "noopener noreferrer";
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  } else {
                                    setMessage({
                                      type: "error",
                                      text: "Invalid ID card URL. Please contact support.",
                                    });
                                  }
                                }
                              }}
                            >
                              Download
                            </MDButton>
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                  )}

                  <MDBox mb={3} />

                  {/* Personal Photo */}
                  {owner.personalPhoto && owner.personalPhoto.url && (
                    <Grid item xs={12} sx={{ minWidth: 0 }}>
                      <MDTypography variant="h6" fontWeight="bold" mb={2}>
                        Personal Photo
                      </MDTypography>
                      <Card sx={{ p: { xs: 2, sm: 2 }, overflow: "hidden" }}>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          flexWrap="wrap"
                          gap={2}
                          sx={{ "& > *": { minWidth: 0 } }}
                        >
                          <Avatar
                            src={owner.personalPhoto.url}
                            sx={{
                              width: { xs: 64, sm: 80 },
                              height: { xs: 64, sm: 80 },
                              flexShrink: 0,
                            }}
                          />
                          <MDBox flex="1 1 120px" minWidth={0}>
                            <MDTypography variant="body2" fontWeight="medium">
                              Personal Photo
                            </MDTypography>
                          </MDBox>
                          <MDBox
                            sx={{
                              flex: { xs: "1 1 100%", sm: "0 0 auto" },
                              minWidth: 0,
                              width: { xs: "100%", sm: "auto" },
                            }}
                          >
                            <Link
                              href={owner.personalPhoto.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                fullWidth
                                sx={{ width: { xs: "100%", sm: "auto" } }}
                                startIcon={<Icon>open_in_new</Icon>}
                              >
                                View Photo
                              </MDButton>
                            </Link>
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                  )}

                  <MDBox mb={3} />

                  {/* No Documents Message */}
                  {(!owner.officialDocuments || owner.officialDocuments.length === 0) &&
                    (!owner.documentBucket || owner.documentBucket.length === 0) &&
                    !owner.idCard?.url &&
                    !owner.personalPhoto?.url && (
                      <Grid item xs={12}>
                        <MDBox textAlign="center" py={6}>
                          <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
                            description
                          </Icon>
                          <MDTypography variant="h6" color="text.secondary" mb={1}>
                            No Documents Found
                          </MDTypography>
                          <MDTypography variant="body2" color="text.secondary">
                            This owner hasn't uploaded any documents yet.
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    )}
                </Grid>
              </MDBox>
            )}

            {/* Edit Information Tab */}
            {activeTab === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    Edit Property Owner Information
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Owner Type *</InputLabel>
                    <Select
                      value={editType}
                      onChange={(e) => setEditType(e.target.value)}
                      label="Owner Type *"
                      sx={{ height: "40px" }}
                    >
                      <MenuItem value="Standard">Standard</MenuItem>
                      <MenuItem value="VIP">VIP</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Notes"
                    placeholder="Add notes about this property owner..."
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    disabled={submitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDBox display="flex" gap={2} justifyContent="flex-end">
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setEditNotes(owner.notes || "");
                        setEditType(owner.type || "Standard");
                      }}
                      disabled={submitting}
                    >
                      Reset
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={handleSave}
                      disabled={submitting}
                      startIcon={<Icon>save</Icon>}
                    >
                      {submitting ? "Saving..." : "Save Changes"}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PropertyOwnerProfile;
